import React, { useEffect, useState, useContext } from 'react';
import * as Mui from '@mui/material';
import useOperationTranslation from '../../hooks/useOperationTranslation';
import { Paper } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import VehicleExitService from './service/VehicleExitService';
import { ContextOperation } from '../../context/OperationContext';
import useSnackbar from '../../hooks/useSnackbar';
import Title from './Title';
import Item from './Item';
import { ConfirmStateValues, ExitConfirmData } from './types';
import { formatTypeString } from '../vehicle-entrance/utils';
import { useTranslation } from 'react-i18next';
import { GlobalContext } from '../../../../util/state/GlobalContextProvider';
import { Parser } from '../../utils';
import FileSaver from 'file-saver';
import * as MuiIcons from '@mui/icons-material';
import { AxiosError } from "axios";
import OperationTypes from "../../types";

interface Props {}

const VehicleExitConfirm = (props: Props) => {
    const { setLoading } = useContext(ContextOperation);
    const t = useOperationTranslation();
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const { state }: any = useLocation();
    const [exitConfirmData, setExitConfirmData] = useState<ExitConfirmData | null>(null);
    const { i18n } = useTranslation();
    const { globalData } = useContext(GlobalContext);
    const [user, setUser] = useState<any>(globalData.user);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [deliveryNoteNumber, setDeliveryNoteNumber] = useState(null);

    useEffect(() => {
        if (state === null || typeof state === 'undefined') {
            navigate('/operation/transfer/vehiclesExit');
        } else {
            setExitConfirmData({
                data: {
                    leftSide: {
                        title: t('vehicleExit.confirm.leftTitle'),
                        drivers: state ? (state as ConfirmStateValues).drivers : [],
                        data: [
                            {
                                label: t('vehicleExit.confirm.vehicle'),
                                value: (state as ConfirmStateValues).modelType,
                            },
                            {
                                label: t('vehicleExit.confirm.plate'),
                                value: (state as ConfirmStateValues).plate,
                            },
                            {
                                label: t('vehicleExit.confirm.status'),
                                value: (state as ConfirmStateValues).chipLabel,
                            },
                        ],
                        damageData: [
                            {
                                label: t('vehicleExit.confirm.damageCode'),
                                value: (state as ConfirmStateValues).damage?.code,
                            },

                            {
                                label: t('vehicleExit.confirm.damageEventType'),
                                value: (() => {
                                    const code = (state as ConfirmStateValues).damage?.eventTypeCode;

                                    if (code) {
                                        return t(`masterDataDamage.eventType.${code?.toLowerCase().replaceAll('.', '_')}` as any);
                                    } else {
                                        return (state as ConfirmStateValues).damage?.eventType;
                                    }
                                })(),
                            },

                            {
                                label: t('vehicleExit.confirm.damageEvent'),
                                value: (() => {
                                    const code = (state as ConfirmStateValues).damage?.eventCode;

                                    if (code) {
                                        return t(`masterDataDamage.event.${code?.toLowerCase().replaceAll('.', '_')}` as any);
                                    } else {
                                        return (state as ConfirmStateValues).damage?.eventLabel;
                                    }
                                })(),
                            },
                        ],
                    },
                    rightSide: {
                        title: t('vehicleExit.confirm.rightTitle'),
                        data: [
                            {
                                label: t('vehicleExit.confirm.from'),
                                value:
                                    (state as ConfirmStateValues).fromLocation.locationEntityDesc +
                                    ` (${formatTypeString((state as ConfirmStateValues).fromLocation.type, t)})`,
                            },
                            {
                                label: t('vehicleExit.confirm.address'),
                                value:
                                    (state as ConfirmStateValues).fromLocation.address +
                                    ', ' +
                                    (state as ConfirmStateValues).fromLocation.streetNumber +
                                    ` (${(state as ConfirmStateValues).fromLocation.zone})`,
                            },
                            {
                                label: t('vehicleExit.confirm.to'),
                                value:
                                    (state as ConfirmStateValues).to.value +
                                    ` (${formatTypeString((state as ConfirmStateValues).to.type, t)})`,
                            },
                            {
                                label: t('vehicleExit.confirm.address'),
                                value:
                                    (state as ConfirmStateValues).to.address +
                                    ', ' +
                                    (state as ConfirmStateValues).to.streetNumber +
                                    ` (${(state as ConfirmStateValues).to.zone})`,
                            },
                            {
                                label: t('vehicleExit.confirm.distance'),
                                value:
                                    Parser.distance((state as ConfirmStateValues).distance?.km) ??
                                    t('vehicleExit.status.distanceError'),
                            },
                            {
                                label: t('vehicleExit.status.expectedArrivalDate'),
                                value: dayjs((state as ConfirmStateValues).endDate).format('DD-MM-YYYY HH:mm'),
                            },
                        ],
                    },
                },
            });
        }
    }, [state, i18n?.language]);

    const handleOnClick = () => {
        setLoading(true);
        const confirmTenant: string = (state as { tenant: string }).tenant;
        const vehicleDamagesCheckOutInsert = {
            damageFormId: (state as ConfirmStateValues)?.damage?.id ?? null,
            number: (state as ConfirmStateValues)?.damage?.code ?? null,
        };
        const body = {
            transferRequestCheckOutInsert: {
                startStationType: (state as ConfirmStateValues).physicalLocationType,
                startStationId: (state as ConfirmStateValues).physicalLocationId,
                startDate: dayjs().toISOString(),
                expectedEndStationId: (state as ConfirmStateValues).to.bpCode,
                expectedEndStationType: (state as ConfirmStateValues).to.type,
                expectedEndDate: (state as ConfirmStateValues).endDate,
                note: '',
                operatorCode: user.username,
            },
            deliveryNoteVehicleCheckOutInsert: {
                vehicleId: (state as ConfirmStateValues).id,
                licensePlate: (state as ConfirmStateValues).plate,
                model: (state as ConfirmStateValues).modelType,
                vin: (state as ConfirmStateValues).vin,
                weight: 0,
                km: 0,
                tank: 'string',
                departureKm: 0,
                departureFuel: 0,
                departureDamageComponent: 'string',
                departureDamageSeverity: 'string',
            },
            vehicleDamagesCheckOutInsert: (state as ConfirmStateValues)?.damage?.code
                ? [vehicleDamagesCheckOutInsert]
                : [],
            driversId: (state as ConfirmStateValues).drivers.map((driver: any) => driver.id),
        };

        VehicleExitService.add
            .confirmExit(confirmTenant, body)
            .then(
                (response) => {
                    setLoading(false);
                    snackbar.show(
                        'success',
                        t('vehicleExit.confirm.success').replace('#plateNotFound', (state as ConfirmStateValues).plate),
                    );
                    setDeliveryNoteNumber(response.deliveryNoteId);
                    setShowDownloadModal(true);
                },
                (error: AxiosError<OperationTypes.AxiosErrorResponse>) => {
                    setLoading(false);

                    if (error.response?.status === 400 && error.response.data.subCode === 10) {
                        snackbar.show('error', t('vehicleExit.confirm.400ErrorSubCode10'));
                    }
                    else if (error.response?.status === 400) {
                        snackbar.show("error", t("vehicleExit.confirm.400Error"));
                    }
                    else if (error.response?.status === 404) {
                        snackbar.show('error', t('vehicleExit.confirm.404Error').replace('#plateNotFound', (state as ConfirmStateValues).plate));
                    }
                    else {
                        snackbar.show("error", t("vehicleExit.confirm.500Error"));
                    }
                },
            );
    };

    const downloadDeliveryNote = () => {
        setLoading(true);
        const confirmTenant: string = (state as { tenant: string }).tenant;
        VehicleExitService.get
            .downloadDeliveryNote(confirmTenant, deliveryNoteNumber)
            .then((response: any) => {
                setLoading(false);
                const filename = response.headers['content-disposition'].split('filename=')[1];
                const blob = new Blob([response.data], { type: 'application/pdf' });
                FileSaver.saveAs(blob, filename);

                navigate(`/operation/transfer/vehiclesExit/`);
            })
            .catch(() => {
                snackbar.show('error', t('vehicleExit.confirm.genericError'));
            });
    };

    return (
        <Mui.Box bgcolor="#fff" padding={{ md: 2, xs: 0 }}>
            <Paper sx={{ py: 2, px: 2 }}>
                <Mui.Stack direction="row" alignItems="center" spacing={2}>
                    <Mui.Button size="icon" variant="text" onClick={() => navigate("/operation/transfer/vehiclesExit/status", { state })}>
                        <MuiIcons.ArrowBack />
                    </Mui.Button>
                    <Mui.Typography variant="h4" textAlign="center">
                        {t('vehicleExit.confirm.checkData')}
                    </Mui.Typography>
                </Mui.Stack>
                <Mui.Grid container spacing={{ md: 2 }} p={{ md: 2 }}>
                    <Mui.Grid item lg={6} md={6} xs={12} sx={{ pb: 2 }}>
                        <Title>{exitConfirmData?.data.leftSide.title}</Title>
                        <Mui.Grid container spacing={2}>
                            {exitConfirmData &&
                                exitConfirmData.data.leftSide.drivers &&
                                exitConfirmData.data.leftSide.drivers.map((driver, index) => {
                                    return (
                                        <React.Fragment key={index}>
                                            <Mui.Grid item xs={6}>
                                                <Item>{`Driver ${index + 1}:`}</Item>
                                                <Item style={{ fontWeight: 700 }}>{driver.label}</Item>
                                            </Mui.Grid>
                                        </React.Fragment>
                                    );
                                })}

                            <Mui.Grid item xs={6}>
                                <Item>{exitConfirmData?.data.leftSide.data[0].label}:</Item>
                                <Item style={{ fontWeight: 700 }}>{exitConfirmData?.data.leftSide.data[0].value}</Item>
                            </Mui.Grid>
                            <Mui.Grid item xs={6}>
                                <Item>{exitConfirmData?.data.leftSide.data[1].label}:</Item>
                                <Item style={{ fontWeight: 700 }}>{exitConfirmData?.data.leftSide.data[1].value}</Item>
                            </Mui.Grid>
                            <Mui.Grid item xs={6}>
                                <Item>{exitConfirmData?.data.leftSide.data[2].label}:</Item>
                                <Item style={{ fontWeight: 700 }}>{exitConfirmData?.data.leftSide.data[2].value}</Item>
                            </Mui.Grid>
                            {state?.damage?.code &&
                                exitConfirmData?.data.leftSide.damageData.map((damage, index) => {
                                    return (
                                        <Mui.Grid key={`${state.damage.code}-${index}`} item xs={6}>
                                            <Item>{damage.label}:</Item>
                                            <Item style={{ fontWeight: 700 }}>{damage.value}</Item>
                                        </Mui.Grid>
                                    );
                                })}
                        </Mui.Grid>
                    </Mui.Grid>
                    <Mui.Grid item lg={6} md={6} xs={12} sx={{ pb: 2 }}>
                        <Title>{exitConfirmData?.data.rightSide.title}</Title>
                        <Mui.Grid container spacing={2}>
                            <Mui.Grid item xs={6}>
                                <Item>{exitConfirmData?.data.rightSide.data[0].label}:</Item>
                                <Item style={{ fontWeight: 700 }}>{exitConfirmData?.data.rightSide.data[0].value}</Item>
                            </Mui.Grid>
                            <Mui.Grid item xs={6}>
                                <Item>{exitConfirmData?.data.rightSide.data[1].label}:</Item>
                                <Item style={{ fontWeight: 700 }}>{exitConfirmData?.data.rightSide.data[1].value}</Item>
                            </Mui.Grid>
                            <Mui.Grid item xs={6}>
                                <Item>{exitConfirmData?.data.rightSide.data[2].label}:</Item>
                                <Item style={{ fontWeight: 700 }}>{exitConfirmData?.data.rightSide.data[2].value}</Item>
                            </Mui.Grid>
                            <Mui.Grid item xs={6}>
                                <Item>{exitConfirmData?.data.rightSide.data[3].label}:</Item>
                                <Item style={{ fontWeight: 700 }}>{exitConfirmData?.data.rightSide.data[3].value}</Item>
                            </Mui.Grid>
                            <Mui.Grid item xs={6}>
                                <Item>{exitConfirmData?.data.rightSide.data[4].label}:</Item>
                                <Item style={{ fontWeight: 700 }}>{exitConfirmData?.data.rightSide.data[4].value}</Item>
                            </Mui.Grid>
                            <Mui.Grid item xs={6}>
                                <Item>{exitConfirmData?.data.rightSide.data[5].label}:</Item>
                                <Item style={{ fontWeight: 700 }}>{exitConfirmData?.data.rightSide.data[5].value}</Item>
                            </Mui.Grid>
                        </Mui.Grid>
                    </Mui.Grid>
                </Mui.Grid>
                <Mui.Button variant="contained" color="accent" fullWidth={true} onClick={handleOnClick}>
                    {t('vehicleExit.confirm.confirmVehicle')}
                </Mui.Button>
            </Paper>

            <Mui.Dialog open={showDownloadModal}>
                <Mui.DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                    <MuiIcons.InfoRounded color="secondary" sx={{ fontSize: '20px', marginRight: 2 }} />
                    {t('vehicleExit.confirm.dialog.title')}
                </Mui.DialogTitle>

                <Mui.DialogContent>{t('vehicleExit.confirm.dialog.body')}</Mui.DialogContent>

                <Mui.DialogActions>
                    <Mui.Button variant="contained" size="small" onClick={() => downloadDeliveryNote()}>
                        {t('vehicleExit.confirm.dialog.buttons.yes')}
                    </Mui.Button>

                    <Mui.Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                            setShowDownloadModal(false);
                            navigate(`/operation/transfer/vehiclesExit/`);
                        }}
                    >
                        {t('vehicleExit.confirm.dialog.buttons.no')}
                    </Mui.Button>
                </Mui.DialogActions>
            </Mui.Dialog>
        </Mui.Box>
    );
};

export default VehicleExitConfirm;
